import { useMemo } from 'react';
import { useGetTicketTypes } from '@openapi/ticket-types/ticket-types';
import { useGetTicketOptions } from '@openapi/ticket-options/ticket-options';

/**
 * Shared hook for fetching ticket types and options for a show.
 * Used by price matrix components.
 *
 * @param {Object} options
 * @param {number|string} options.showId - The show ID
 * @param {boolean} options.includeSeats - Whether to include seats in ticket types (default: false)
 * @param {Function} options.onError - Error callback (optional)
 * @returns {Object} { ticketTypes, ticketOptions, loading, error }
 */
const useTicketMatrixData = ({ showId, includeSeats = false, onError }) => {
    const {
        data: ticketTypesData,
        isLoading: ticketTypesLoading,
        error: ticketTypesError,
    } = useGetTicketTypes(
        {
            optionalId: Number(showId),
            page: 0,
            pageSize: 1000,
            ...(includeSeats && { includeSeats: true }),
        },
        {
            query: {
                enabled: !!showId,
                ...(onError && { onError }),
            },
        },
    );

    const {
        data: ticketOptionsData,
        isLoading: ticketOptionsLoading,
        error: ticketOptionsError,
    } = useGetTicketOptions(
        {
            optionalId: Number(showId),
            page: 0,
            pageSize: 1000,
        },
        {
            query: {
                enabled: !!showId,
                ...(onError && { onError }),
            },
        },
    );

    const ticketTypes = useMemo(() => ticketTypesData?.ticketTypes || [], [ticketTypesData]);

    const ticketOptions = useMemo(
        () => ticketOptionsData?.ticketOptions || [],
        [ticketOptionsData],
    );

    return {
        ticketTypes,
        ticketOptions,
        loading: ticketTypesLoading || ticketOptionsLoading,
        error: ticketTypesError || ticketOptionsError,
    };
};

export default useTicketMatrixData;
